/* tslint:disable:no-string-literal */
import {Injectable} from "@angular/core";
import {Observable, Subscription} from "rxjs";
import {
    ISelection, IGlossaryEntry, IDictionaryEntry,
    IText, ISentence, IWord, IMetaData, WordFormatType
} from "../../api/data-model";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {HttpClient} from "@angular/common/http";
import {parse} from "himalaya";
import {AudioState} from "../../constants";
import {IExpansions} from "./batch.pipeline.service";
import {DictionaryPipelineService} from "./dictionary.pipeline.service";
import {UtilitiesService} from "../utilities.service";

enum Attribute {
    TITLE = "MsoTitle",
    TITLE_ALT = "MsoTitleCxSpFirst",
    NORMAL = "MsoNormal",
    COMMENT = "MsoCommentReference",
    COMMENT_ANCHOR = "msocomanchor",
    COMMENT_TEXT = "msocomtxt"
}

enum Tag {
    SPAN = "span",
    HEADER = "h1",
    BOLD = "b",
    ITALIC = "i",
    IMAGE = "img"
}

enum Type {
    TEXT = "text",
    ELEMENT = "element"
}
enum TextStyle {
    COMMENT = "mso-element:comment"
}

// TODO:
//  apply glue word coding, abbreviations - move these files to rav2-content-data
//  process images

@Injectable()
export class ConversionPipelineService {
    public static currentSourceSelection: ISelection;
    // TODO: this list is not complete, may need smart quotes etc
    public static PUNCTUATION = /[,&.?;:'"!-]/g;
    public static SPACE = /\s|&nbsp;| |\r\n/g;
    public static CONTENT_GROUP = "CONTENT_2022_2023";
    public static UPCOMING_RELEASES = ["6", "7"];
    private sentenceId: number;
    private wordId: number;
    private expansions: IExpansions;

    constructor(
        private logService: BugsnagLoggingService,
        private dictionaryService: DictionaryPipelineService,
        private http: HttpClient
    ) {
        this.dictionaryService.loadExpansions().subscribe((response: any) => {
            this.expansions = (response as IExpansions);
            Object.keys(this.expansions).forEach((key) => {
                if (!this.expansions[key.toLowerCase()]) {
                    this.expansions[key.toLowerCase()] = this.expansions[key];
                }
            });
        });
    }

    public convert(html): ISelection[] {
        // TODO: one selection for now
        const jsonHtml = parse(html);
        console.log(JSON.stringify(jsonHtml, null, 4));
        let selection = this.createSelection(jsonHtml);
        // if we couldn't identify the reference name, don't save the selection, to avoid cleanup later.
        return selection?.metaData?.referenceStory ? [selection] : [];
    }

    private createSelection(jsonHtml: Array<object>): ISelection {
        this.sentenceId = 1;
        this.wordId = 1;
        let metaData: IMetaData = {
            referenceStory: "",
            credits: [],
            ATOSGradeEquivalent: "",
            contentVersion: ConversionPipelineService.CONTENT_GROUP,
            coverImage: "",
            destructiveChangeTag: "",
            lexileLevel: "",
            fountasPinnellLevel: "",
            fullTitle: "",
            shortTitle: "",
            introAudio: "",
            audioState: AudioState.UNRECORDED,
            titleAudio: "",
            genre: "",
            introText: "",
            topic: "",
            wordCount: "",
            wpmGoal: ""
        };
        let selection: ISelection = {metaData, text: [], questionEntries: []};
        // get title and put it in metadata
        this.addTitle(jsonHtml, selection);
        // get text elements and process (including header formats)
        // postprocess to add word audio, word count
        this.addSelection(jsonHtml, selection, false, false);
        let count = selection.text.reduce((acc, item) => {
                let numWords = item.sentences ? item.sentences.reduce((accWords, sentence) => {
                    let num = sentence.words ? sentence.words.length : 0;
                    return accWords + num;
                }, 0) : 0;
                return acc + numWords; }
            , 0);
        selection.metaData.wordCount = count.toString();
        return selection;

    }

    private addTitle(htmlItems: any[], selection: ISelection): boolean {
        if (!htmlItems || !Array.isArray(htmlItems)) {
            return false;
        }
        for (let htmlObj of htmlItems) {
            if (this.hasClass(htmlObj["attributes"], Attribute.TITLE) || this.hasClass(htmlObj["attributes"], Attribute.TITLE_ALT)) {
                let text = this.findText(htmlObj["children"]).trim();
                if (text.replace(ConversionPipelineService.SPACE, "")) {
                    selection.metaData.fullTitle = text;
                    selection.metaData.shortTitle = text;
                    selection.metaData.referenceStory = UtilitiesService.convertPunctuationToASCII(
                        text).replace(ConversionPipelineService.PUNCTUATION, "")
                        .replace(ConversionPipelineService.SPACE, "");
                    console.log("reference: " + selection.metaData.referenceStory);
                    return true;
                }
            }
            if (this.addTitle(htmlObj["children"], selection)) {
                return true;
            }
        }
        return false;
    }

    private addSelection(htmlItems: any[], selection: ISelection,
                         foundTextMarker: boolean, foundHeadingMarker: boolean): void {
        if (!htmlItems || !Array.isArray(htmlItems)) {
            return;
        }
        for (let htmlObj of htmlItems) {
            if (foundTextMarker) {
                if (htmlObj["type"] === Type.TEXT) {
                    this.addText(htmlObj["content"], foundHeadingMarker, selection);
                } else if (htmlObj["tagName"] === Tag.IMAGE) {
                    this.addImage(htmlObj, selection);
                }
            }
            let nextLevelTextMarker = (foundTextMarker || this.hasClass(htmlObj["attributes"], Attribute.NORMAL)) &&
                !this.isExcludedText(htmlObj);
            const nextLevelHeadingMarker = (foundHeadingMarker || htmlObj["tagName"] === Tag.HEADER)
                /*&& foundTextMarker*/;
            if (htmlObj["tagName"] === Tag.HEADER) {
                console.log("found header " + " text " + foundTextMarker + "next " + nextLevelHeadingMarker);
                nextLevelTextMarker = true;
            }
            if (!this.hasClass(htmlObj["attributes"], Attribute.COMMENT_TEXT)) {
                if (this.isFormattingSpan(htmlObj) && nextLevelTextMarker) {
                    const text = this.flattenSpan(htmlObj["children"]);
                    this.addText(text, nextLevelHeadingMarker, selection);
                } else {
                    this.addSelection(htmlObj["children"], selection, nextLevelTextMarker, nextLevelHeadingMarker);
                }
            }
        }
        return;
    }

    private isExcludedText(htmlObj) {
        // do not want to extract text which is either a comment (not part of the main text), or the title (stored elswhere)
        return this.hasClass(htmlObj["attributes"], Attribute.COMMENT) ||
            this.hasClass(htmlObj["attributes"], Attribute.COMMENT_ANCHOR) ||
            this.hasClass(htmlObj["attributes"], Attribute.TITLE) ||
            this.hasClass(htmlObj["attributes"], Attribute.TITLE_ALT);
    }

    private addImage(htmlObj, selection: ISelection) {
        let imageAttr = htmlObj["attributes"]?.find((attr => attr["key"] === "src"));
        let imageSrc = imageAttr ? imageAttr["value"] : "";
        let imageShapesAttr = htmlObj["attributes"]?.find((attr => attr["key"] === "v:shapes"));
        if (imageShapesAttr && !imageSrc) {
            imageSrc = "Unknown";
        }
        imageAttr = htmlObj["attributes"]?.find((attr => attr["key"] === "height"));
        const imageHeight = imageAttr ? imageAttr["value"] : "";
        imageAttr = htmlObj["attributes"]?.find((attr => attr["key"] === "width"));
        const imageWidth = imageAttr ? imageAttr["value"] : "";
        if (imageSrc) {
            imageSrc = selection.metaData.referenceStory + "_" +
                imageSrc.replace(/.*\//, "").replace(/\.png$/i, ".jpg");
            let media: IText = {
                mediaFormat: {
                    source: imageSrc,
                    type: "img",
                    height: imageHeight,
                    width: imageWidth
                }
            };
            selection.text.push(media);
        }
    }

    private isFormattingSpan(htmlObj): boolean {
        if (htmlObj["tagName"] === Tag.SPAN) {
            const isFormatSpan = htmlObj["children"] && htmlObj["children"].every(child => child["type"] === Type.TEXT ||
                (child["type"] === Type.ELEMENT && (child["tagName"] === Tag.BOLD || child["tagName"] === Tag.ITALIC))
                || !child["children"] || child["children"].length === 0);
            return isFormatSpan;
        }
        return false;
    }

    private flattenSpan(htmlItems: any[]): string {
        let text = "";
        for (let htmlObj of htmlItems) {
            // for now assume depth 1 or 2 only
            if (htmlObj["type"] === Type.TEXT) {
                text += htmlObj["content"].replace("\r\n", " ");
            } else if (htmlObj["type"] === Type.ELEMENT) {
                let format = "";
                switch (htmlObj["tagName"]) {
                    case Tag.BOLD:
                        format = "<bold>";
                        break;
                    case Tag.ITALIC:
                        format = "<italic>";
                        break;
                    default:
                        break;
                }
                let newText = htmlObj["children"].filter(child => child["type"] === Type.TEXT &&
                    !!child["content"].replace(ConversionPipelineService.SPACE, ""))
                    .map(child => child["content"].replace("\r\n", " ")).join();
                if (!!newText.replace(ConversionPipelineService.SPACE, "")) {
                    newText = newText.split(/\s+/).map(word => !!word ? format + word : word).join(" ");
                    text += newText;
                }
            }

        }
        return text;
    }

    private addText(text, foundHeadingMarker: boolean, selection: ISelection) {
        // push element text into selection
        text = text.replace("\r\n", " ");
        if (text.replace(ConversionPipelineService.SPACE, "")) {
            const words = this.getWords(text.trim());
            if (words.length) {
                selection.text.push(this.parseSentences(words, foundHeadingMarker));
            }
        }
    }

    private getWords(paragraph: string): string[] {
        paragraph = paragraph.replace(". . . .", "....");
        paragraph = paragraph.replace(/[ ]?\. \. \. ,/g, "...,");
        paragraph = paragraph.replace(/[ ]?\. \. \./g, "...");
        paragraph = paragraph.replace(/…([A-Za-z0-9])/g, "… $1");
        paragraph = paragraph.replace(/^([.]+) /, "$1"); // leading '...'
        paragraph = paragraph.replace(/—(\S)/g, "— $1"); // em-dash without space causes word parsing issues
        let paragraphWords = paragraph.split(/\s+/)
            .filter((w) => (w && w.trim() !== ""));
        return paragraphWords;
    }

    private parseSentences(paragraphWords: string[], isHeading: boolean): IText
    {
        let sentence: ISentence = {audio: "", audioState: AudioState.UNRECORDED, id: this.sentenceId.toString(10), words: []};
        let text: IText = {sentences: []};
        let inSingleQuotePhrase = false;
        paragraphWords.forEach((paragraphWord: string, index: number) => {
            // split into prefix, word, separator
            // let match = word.match(/^([("“”]*)([^, "“”,.?!]+)([, "“”,.?!]*)/);
            let match: RegExpMatchArray;
            // digit and clock sequences can have , : and . internally, urls can have . internally
            if (paragraphWord.match(/www[.]/)) {
                match = paragraphWord.match(/^([\[("“”‘—]*)([^, "“”;:?!—)…]*[^, "“”;:.?!—)…\]]+)(.*)/);

            } else if (paragraphWord.match(/\d/)) {
                match = paragraphWord.match(/^([\[("“”‘—]*)([^ "“”;?!—)…]*[^, "“”;:.?!—)…\]]+)(.*)/);

            } else {
                match = paragraphWord.match(/^([\[("“”‘.—]*)([^, "“”;:.?!—)…\]]+)(.*)/);
            }
            if (!match) {
                console.error("No match for: " + paragraphWord  + " in " + paragraphWords.join("|"));
            }
            if (!match[1]) {
                match[1] = "";
            } else {
                console.log("got formatting match:" + match[1]);
            }

            let word: IWord;
            const endMatch = match[3].endsWith(",") ? match[3].slice(0, -1) : match[3];
            const isAbbreviation = endMatch && endMatch.endsWith(".") && !!this.expansions[(match[2] + endMatch).toLowerCase()];
            const hasComma = match[3].endsWith(",");
            if (isAbbreviation) {
                let abbrev = match[2] + endMatch;
                word = {
                    id: this.wordId.toString(10),
                    audio: "",
                    category: "2",
                    prefix: match[1],
                    word: abbrev,
                    dictionary: this.expansions[(match[2] + endMatch).toLowerCase()],
                    separator: hasComma ? ", " : " "
                };
            } else {
                let separator = match[3];
                if ((separator !== "—") && (separator !== "…")) {
                    separator += " ";
                }
                word = {
                    id: this.wordId.toString(10),
                    audio: "",
                    category: "2",
                    prefix: match[1],
                    word: match[2],
                    separator
                };
            }
            this.applyFormat(word);
            if (word.prefix.indexOf("‘") >= 0) {
                inSingleQuotePhrase = true;

            }
            if (inSingleQuotePhrase && word.word.substr(-1) === "’") {
                // this is the close of the single quote phrase, not an apostrophe.
                word.separator = "’" + word.separator;
                word.word = word.word.substr(0, word.word.length - 1);
                inSingleQuotePhrase = false;
            }
            if (this.tableExpansion(word.word)) {
                word.dictionary = [this.tableExpansion(word.word)];
            }
            else if (this.ruleExpansion(word.word)) {
                word.dictionary = [this.ruleExpansion(word.word)];
            }
            this.wordId++;

            sentence.words.push(word);
            let nextOne = "";
            let oneAfterThat = "";

            if (paragraphWords.length > index + 1) {
                nextOne = paragraphWords[index + 1];
            }
            if (paragraphWords.length > index + 2) {
                oneAfterThat = paragraphWords[index + 2];
            }
            if (isAbbreviation) {
                this.applyAbbreviationRules(word, nextOne);
            }

            if (this.isSentenceEnd(word, nextOne, oneAfterThat) && sentence.words.length) {
                text.sentences.push(JSON.parse(JSON.stringify(sentence)));
                this.sentenceId++;
                sentence = {audio: "", audioState: AudioState.UNRECORDED, id: this.sentenceId.toString(10), words: []};

            }

        });

        if (sentence.words.length) {
            // whatever is left at end of paragraph gets pushed regardless
            text.sentences.push(JSON.parse(JSON.stringify(sentence)));
            this.sentenceId++;
        }
        if (isHeading) {
            text.formats = ["heading"];
        }
        return text;

    }

    private applyFormat(word: IWord): void {
        if (word.word.startsWith("<")) {
            let format = word.word.match(/<(bold|italic)>/);
            if (!!format[1]) {
                word.word = word.word.replace(/<(bold|italic)>/, "");
                word.formats = [format[1] as WordFormatType];
            }
        }
    }



    private hasClass(htmlAttributes: object, className: string): boolean {
        if (!htmlAttributes || !Array.isArray(htmlAttributes)) {
            return false;
        }
        return !!htmlAttributes.find(attribute => attribute["key"] === "class" && attribute["value"] === className);
    }

    private findText(htmlChildren: object): string {
        if (!htmlChildren || !Array.isArray(htmlChildren)) {
            return "";
        }
        let text = "";
        for (let htmlChild of htmlChildren) {
            if (htmlChild["type"] === "text"){
                text = htmlChild["content"].replace("\r\n", " ");
                if (text.replace(ConversionPipelineService.SPACE, "")) {
                    return text;
                }
            }
            text = this.findText(htmlChild["children"]);
            if (text) {
                return text;
            }
        }
        return text;
    }

    private applyAbbreviationRules(word: IWord, next: string): void {
        // In general the abbreviation does not mark the end of a sentence, but in some cases it does
        const sequence = word.word + word.separator + next;
        // TODO: rather than continuing to rack up ad-hoc rules here, really should have some way to fix these in the CPT
        const rules = ["Jr. They"];
        if (rules.find(r => r === sequence)  && next.match(/^[A-Z]/) && word.word.endsWith(".")) {
            word.word = word.word.replace(/[.]$/, "");
            word.separator = "." + word.separator;
        }
    }

    private tableExpansion(wordString: string): string {
        // TODO: will deal with text expansions later
        /*if (this.expansionsMap[wordString.toLowerCase()]) {
            return this.expansionsMap[wordString.toLowerCase()];
        }*/
        return "";
    }

    private ruleExpansion(wordString: string): string {
        let expansion: string;
        expansion = wordString.replace(/’/g, "'");
        expansion = expansion.replace(/-/g, " ");
        expansion = this.numbersExpansion(expansion);
        // TODO: need to pull in wordAudioService etc here
        // todo: should probably do this from dictionary, not word audio; and need to mark for review somehow
        /*if (!this.wordAudioService.wordAudioTable[expansion.toLowerCase()]) {
            // homographs - just taking the more common for now
            if (this.wordAudioService.wordAudioTable[expansion.toLowerCase() + "!1"]) {
                expansion = expansion.toLowerCase() + "!1";
            }
        }*/
        if (expansion !== wordString) {
            return expansion;
        }
        return "";

    }

    private numbersExpansion(wordString: string): string {
        let expansion = wordString;
        // only try to expand one or two digit numbers, or hundreds
        let digitTeensExpansion = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
            "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
            "seventeen", "eighteen", "nineteen"];
        let tensExpansion = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

        if (expansion.match(/^[0-9][0-9]?$/)) {
            let num = parseInt(expansion, 10);
            if (num < 20) {
                expansion = digitTeensExpansion[num];
            }
            else {
                let numberTens = parseInt(expansion[0], 10);
                let numberOnes = parseInt(expansion[1], 10);
                expansion = tensExpansion[numberTens] + " " + digitTeensExpansion[numberOnes];

            }

        }
        else if (expansion.match(/^[1-9]00$/)) {
            let num = parseInt(expansion[0], 10);
            expansion = digitTeensExpansion[num] + " hundred";
        }
        return expansion.trim();
    }

    private isSentenceEnd(word: IWord, nextOne: string, oneAfterThat: string): boolean
    {
        // tbd: need case normalization?  Sometimes?

        let nextWordLowerCase = nextOne && nextOne.match(/^[a-z]/);
        let nextWordIsDayOfWeek = ["monday.", "tuesday.", "wednesday.", "thursday.", "friday.", "saturday.", "sunday."]
            .indexOf(nextOne.toLowerCase().trim()) > -1;
        let isDateline = word.word.match(/[0-9]/) && nextWordIsDayOfWeek;
        let oneAfterThatIsAttribution = this.matchesAttributionPattern(nextOne, oneAfterThat);

        let attributionFollowsQuote = (word.separator.match(/["“”]/)) && (nextWordLowerCase || oneAfterThatIsAttribution);
        let isASCIIEllipsis = word.separator.match(/^[.][.][.]/);
        if (word.separator.match(/^['’)\]]*[.?!]["“”)]*/)  &&
            !attributionFollowsQuote && !isASCIIEllipsis && !isDateline) {
            console.log("returning true for sentence end for word: " + word.word + " separator: " + word.separator);
            return true;
        }
        // TODO: this may break some reparsing of stories (Malala? Bears Bad Day?)
        if (word.separator.match(/…/)  && !nextWordLowerCase && !attributionFollowsQuote) {
            return true;
        }
        return false;
    }

    // Used to try to identify and parse dialogue correctly.  For example, '"Stop!" I yelled.' is one sentence not two.
    private matchesAttributionPattern(firstWord, secondWord): boolean {
        let phrase = firstWord + " " + secondWord;
        let nonAttributions: Set<string> = new Set(["Jade said"]);
        if (nonAttributions.has(phrase)) {
            return false;
        }


        let attributionVerbs: Set<string> = new Set(["said", "thought", "boasted", "yelled", "moaned",
            "worried", "asked", "asks", "ask", "replies", "guesses", "cries", "shrieked", "begged", "pleaded"]);
        /* console.log("attrib: " + firstWord + " " + secondWord + " " +
                     secondWord.match(/^[^"“”.?!]*!/)[0].toLowerCase() + " " +
                     attributionVerbs.has(secondWord.match(/^[^"“”.?!]*!/)[0].toLowerCase()));*/
        if (secondWord.match(/^[^"“”.?!]*/) && attributionVerbs.has(secondWord.match(/^[^"“”.?!]*/)[0].toLowerCase())) {
            return true;
        }

        let otherCheats: Set<string> = new Set(["Buff the"]);

        if (otherCheats.has(phrase)) {
            return true;
        }

        return false;

    }

}
